import { MutableRefObject, useCallback, useEffect } from "react";

const CAPTURE_EVENT = "capture-outside-event";

export const captureEvent = new Event(CAPTURE_EVENT);

/**
 * This is a utility function designed to trigger the 'click outside' callback
 * in scenarios where a standard click event may not reach the intended target due to interception
 * by other elements or event handlers.
 */
export const dispatchCaptureEvent = () => document.dispatchEvent(captureEvent);

export function useClickOutside(refs: MutableRefObject<any>[], onClickOutside: () => void): void {
    const handleClickOutside = useCallback(
        (e: MouseEvent | typeof captureEvent) => {
            const findRefs = refs.some((ref) => {
                if (ref.current?.getComponent) {
                    return ref.current?.getComponent().ref?.current?.getElement().contains(e.target);
                } else {
                    return ref?.current?.contains(e.target);
                }
            });
            if (!findRefs) {
                onClickOutside();
            }
        },
        [onClickOutside, refs]
    );

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener(CAPTURE_EVENT, handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener(CAPTURE_EVENT, handleClickOutside);
        };
    }, [handleClickOutside]);
}
